import { createSharedComposable } from '@vueuse/core'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import dayjs from 'dayjs'
import { type BannerActionKey, type BannerItems } from '@klausapp/app-navigation/types'

import { hasZendeskProxy } from '@/config'
import { session } from '@/composables/useSession'
import { hasAccountRole } from '@/utils/roleUtils'
import i18n from '@/i18n'
import { useEvent } from '@/composables/useEvent'
import analytics from '@/utils/analytics'

export default createSharedComposable(() => {
  const route = useRoute()
  const router = useRouter()

  // USER LIMIT BANNER
  const userLimitVisible = ref(false)
  useEvent('user-limit-reached', () => (userLimitVisible.value = true))

  const userLimitReachedBanner = computed(() => {
    if (!userLimitVisible.value) return null

    return {
      id: 'user-limit-reached',
      title: i18n.t('modules.application.banners.user_limit.title'),
      type: 'warning' as BannerItems['type'],
      action: {
        label: i18n.t('modules.application.banners.user_limit.link_label'),
        type: 'more',
      },
      dismissable: true,
    }
  })

  // TRIAL BANNERS
  const trialRemainingCount = computed(() => {
    const today = new Date()
    const remaining = dayjs(session.account.trialEndAt).diff(today, 'day')
    return remaining > 0 ? remaining : 0
  })

  const trialRemainingText = computed(() => {
    if (trialRemainingCount.value === 0)
      return hasZendeskProxy && hasAccountRole('ADMIN')
        ? i18n.t('modules.application.banners.trial_end.template_today_zd_only')
        : i18n.t('modules.application.banners.trial_end.template_today')
    else if (trialRemainingCount.value === 1)
      return hasZendeskProxy && hasAccountRole('ADMIN')
        ? i18n.t('modules.application.banners.trial_end.template_1_day_zd_only', { n: trialRemainingCount.value })
        : i18n.t('modules.application.banners.trial_end.template_1_day', { n: trialRemainingCount.value })
    return hasZendeskProxy && hasAccountRole('ADMIN')
      ? i18n.t('modules.application.banners.trial_end.template_days_zd_only', { n: trialRemainingCount.value })
      : i18n.t('modules.application.banners.trial_end.template_days', { n: trialRemainingCount.value })
  })

  const trialEndingVisible = computed(() => {
    const hasActiveTrial = session.account.status === 'active' && session.account.trialEndAt
    return route.name !== 'onboarding' && hasActiveTrial && trialRemainingCount.value < 8 && !hasZDTrial.value
  })

  // TRIAL ENDING BANNER ITEM
  const trialEndingBanner = computed(() => {
    if (!trialEndingVisible.value) return null
    return {
      id: 'trial-ending',
      title: trialRemainingText.value,
      type: 'warning' as BannerItems['type'],
      // TODO?: possibly should separate link from text and add it as action for Admins
    }
  })

  const hideZDTrialBanner = ref(false)
  const hasZDTrial = computed(() => hasZendeskProxy && trialRemainingCount.value > 8 && trialRemainingCount.value < 15)
  const zdTrialVisible = computed(() => hasZDTrial.value && !hideZDTrialBanner.value)

  // IN TRIAL BANNER ITEM
  const accountInTrialBanner = computed(() => {
    if (!zdTrialVisible.value) return null

    return {
      id: 'zd-trial-info',
      title: i18n.t('modules.application.banners.trial_left.template_zd_only', { days: trialRemainingCount.value }),
      type: 'info' as BannerItems['type'],
      dismissable: true,
      ...(hasAccountRole('ADMIN')
        ? {
            action: {
              label: i18n.t('modules.application.banners.account_in_trial.link_label'),
              href: '/admin',
            },
          }
        : {}),
    }
  })

  const globalBanners = computed(() =>
    [userLimitReachedBanner.value, trialEndingBanner.value, accountInTrialBanner.value].filter(
      (banner) => banner !== null,
    ),
  )

  const handleBannerClick = (bannerId: string, action: BannerActionKey) => {
    if (bannerId === 'user-limit-reached') {
      if (action === 'more') return router.push({ name: 'settings.subscription' })
      else {
        userLimitVisible.value = false
        analytics.closeMessageClicked()
      }
    }

    if (bannerId === 'zd-trial-info') {
      hideZDTrialBanner.value = true
      analytics.closeMessageClicked()
    }
  }

  return {
    trialRemainingCount,
    trialRemainingText,
    trialEndingVisible,
    userLimitVisible,
    zdTrialVisible,
    hideZDTrialBanner,
    globalBanners,
    onBannerClick: handleBannerClick,
  }
})
